import type { FC } from 'react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Gender } from 'config/enums';
import { useAppSelector } from 'features/app/hooks';
import { callLanguageCodeSelector } from 'features/call/call-base/store/selectors';
import { usePopoverContext } from 'features/menu/context';
import { TeamingAdhocRequestForm } from 'features/teaming/teaming-adhoc/components';
import { TeamingType } from 'features/teaming/teaming-base/enums';
import type { TeamRequestFormOptions } from 'features/teaming/teaming-base/interfaces';
import { TeamingInPersonRequestForm } from 'features/teaming/teaming-inperson/components';
import { TeamingRemoteRequestForm } from 'features/teaming/teaming-remote/components';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import type { CustomSelectItem } from 'features/ui/elements';
import { getDefaultSelectValue } from 'features/ui/elements';

interface TeamRequestProps {
  reasonOptions: CustomSelectItem[];
  locationOptions: CustomSelectItem[];
}

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 266px;
  padding: ${theme.spacing(9)};
  border-radius: 8px;
  background: ${theme.palette.grey[900]};
`;

export const TeamRequest: FC<TeamRequestProps> = ({
  reasonOptions,
  locationOptions,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { handleClosePopover } = usePopoverContext();
  const language = useAppSelector(callLanguageCodeSelector);

  const {
    control,
    getValues,
    formState: { isValid, errors },
  } = useForm<TeamRequestFormOptions>({
    mode: 'onChange',
    defaultValues: {
      gender: Gender.U,
      reason: getDefaultSelectValue(reasonOptions),
      location: getDefaultSelectValue(locationOptions),
      language,
      adhocUsername: '',
    },
  });

  const selectedTeamingType = useWatch({ control, name: 'location' });

  return (
    <Form data-testid='team-request'>
      {(selectedTeamingType === TeamingType.RemoteAnyRequest ||
        selectedTeamingType === TeamingType.RemoteInCenterRequest) && (
        <TeamingRemoteRequestForm
          closePopover={handleClosePopover}
          setLoading={setIsLoading}
          getFormValues={getValues}
          control={control}
          isLoading={isLoading}
          isValid={isValid}
          reasonOptions={reasonOptions}
          locationOptions={locationOptions}
        />
      )}
      {selectedTeamingType === TeamingType.InCenterAnyRequest && (
        <TeamingInPersonRequestForm
          closePopover={handleClosePopover}
          setLoading={setIsLoading}
          getFormValues={getValues}
          control={control}
          isLoading={isLoading}
          isValid={isValid}
          reasonOptions={reasonOptions}
          locationOptions={locationOptions}
        />
      )}
      {selectedTeamingType === TeamingType.InCenterPreferredRequest && (
        <TeamingAdhocRequestForm
          closePopover={handleClosePopover}
          setLoading={setIsLoading}
          getFormValues={getValues}
          control={control}
          isLoading={isLoading}
          isValid={isValid}
          errors={errors}
          reasonOptions={reasonOptions}
          locationOptions={locationOptions}
        />
      )}
    </Form>
  );
};
